import { Capacitor } from '@capacitor/core';

import { RoisBackgroundLocation, NativeGpsPoint } from '../plugins/roisBackgroundLocation';
import { appendGpsPoints, LocalGpsPoint } from '../offline/gpsTrackStore';

/**
 * Fuente de posición conmutable: `navigator.geolocation` en el navegador, el
 * plugin nativo dentro de la app Android.
 *
 * El objetivo es que `useSurveillanceRouteGpsTracking` NO se entere de cuál de
 * las dos está usando: el filtro de movimiento adaptativo, el keepalive, la
 * escritura en `gpsTrackStore` y el flush al backend siguen exactamente igual.
 * Lo único que cambia es de dónde salen los fixes y si sobreviven a que la
 * pantalla se apague.
 */

/** Opciones que solo entiende la fuente nativa; la web las ignora. */
export interface WatchOptions {
  /** Obligatorio en nativo: el servicio etiqueta cada punto del buffer con él. */
  routeId?: string;
}

export interface PositionSource {
  /**
   * Arranca la captura. Devuelve la función de parada (equivalente a
   * `clearWatch`). Es asíncrona porque en nativo hay que levantar el
   * foreground service y registrar el listener del bridge.
   *
   * La parada recibe `stopBackgroundService`, y distinguirlo importa: quitar
   * el listener y apagar el servicio son dos cosas distintas. Si el operario
   * navega a otra pantalla con el tramo abierto (p. ej. a crear una Actuación
   * básica desde el tramo), el componente se desmonta pero el servicio TIENE
   * que seguir capturando — apagarlo ahí cortaría la traza por el mismo
   * motivo por el que la app existe.
   */
  watch(
    onPosition: (position: GeolocationPosition) => void,
    onError: (error: { code: number; message: string }) => void,
    options?: WatchOptions
  ): Promise<(stopBackgroundService: boolean) => void>;

  /** Lectura puntual (refresco al volver a primer plano, prompt de permisos). */
  getCurrent(
    onPosition: (position: GeolocationPosition) => void,
    onError: (error: { code: number; message: string }) => void
  ): void;

  /**
   * `true` solo en la app nativa. La web no puede capturar en segundo plano y
   * es la razón de existir de toda la envoltura Capacitor.
   */
  isBackgroundCapable: boolean;
}

const WEB_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 2000,
  timeout: 10000,
};

const WEB_ONE_SHOT_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 0,
  timeout: 10000,
};

type GeolocationLike = Pick<
  Geolocation,
  'watchPosition' | 'clearWatch' | 'getCurrentPosition'
>;

/**
 * Convierte un punto del plugin al shape de `GeolocationPosition` que ya
 * consume el hook. El `timestamp` nativo es epoch en ms, igual que
 * `GeolocationPosition.timestamp`, así que el ISO que acaba en
 * `gpsTrackStore` es idéntico venga por donde venga — que es lo que permite
 * que la deduplicación por timestamp absorba el mismo punto llegado por el
 * listener en vivo y por el drenaje del buffer.
 */
function toGeolocationPosition(point: NativeGpsPoint): GeolocationPosition {
  return {
    timestamp: point.timestamp,
    coords: {
      latitude: point.latitude,
      longitude: point.longitude,
      accuracy: point.accuracy ?? 0,
      altitude: null,
      altitudeAccuracy: null,
      heading: point.bearing ?? null,
      speed: point.speed ?? null,
      toJSON() {
        return this;
      },
    },
    toJSON() {
      return this;
    },
  } as GeolocationPosition;
}

function toLocalGpsPoint(point: NativeGpsPoint): LocalGpsPoint {
  return {
    latitude: point.latitude,
    longitude: point.longitude,
    timestamp: new Date(point.timestamp).toISOString(),
    speed: point.speed,
    accuracy: point.accuracy,
    // Señal S3 del detector de cambio de sentido. Se dejaba fuera cuando el
    // almacén era solo para el flush al backend; ahora el detector se alimenta
    // de él, y sin esto perdía el rumbo del dispositivo en la app nativa.
    heading: point.bearing ?? undefined,
  };
}

function createWebPositionSource(geo: GeolocationLike): PositionSource {
  return {
    isBackgroundCapable: false,
    watch(onPosition, onError) {
      const watchId = geo.watchPosition(onPosition, onError, WEB_OPTIONS);
      // En web no hay servicio que apagar: el flag se ignora.
      return Promise.resolve(() => geo.clearWatch(watchId));
    },
    getCurrent(onPosition, onError) {
      geo.getCurrentPosition(onPosition, onError, WEB_ONE_SHOT_OPTIONS);
    },
  };
}

/**
 * El `start()` nativo que está en curso, si lo hay.
 *
 * 🔴 Android atiende UNA petición de permisos a la vez, y `start()` pide la
 * ubicación y las notificaciones que falten. Un segundo `start()` mientras el
 * primero espera a que el operario conteste su diálogo hace que Android lo
 * ignore («Can request only one set of permissions at a time») y que Capacitor
 * dé la PRIMERA petición por denegada. Medido en la W el 25-09-2026: al entrar
 * con un recorrido en curso, el guardián lo repetía cada 15 s con el diálogo
 * abierto y salía el aviso rojo «El registro GPS se ha detenido» con el GPS
 * esperando una respuesta. Quien llega con uno en curso espera a ese mismo.
 */
let startInFlight: { routeId: string; promise: Promise<void> } | null = null;

/** ¿Hay un `start()` esperando (p. ej. a que se conteste su diálogo)? */
export function isNativeStartInFlight(): boolean {
  return startInFlight !== null;
}

function startNativeService(routeId: string): Promise<void> {
  if (startInFlight) {
    if (startInFlight.routeId === routeId) return startInFlight.promise;
    // Otro recorrido: después del que está en curso, nunca a la vez.
    return startInFlight.promise
      .catch(() => undefined)
      .then(() => startNativeService(routeId));
  }
  // Los textos de la notificación NO se pasan desde aquí: viven en
  // `strings.xml` (ca/es/en) porque los muestra Android antes de que el
  // WebView exista, así que i18next no puede resolverlos.
  const promise: Promise<void> = RoisBackgroundLocation.start({ routeId }).finally(() => {
    if (startInFlight?.promise === promise) startInFlight = null;
  });
  startInFlight = { routeId, promise };
  return promise;
}

function createNativePositionSource(): PositionSource {
  return {
    isBackgroundCapable: true,
    async watch(onPosition, onError, options) {
      const routeId = options?.routeId;
      if (!routeId) {
        onError({ code: 2, message: 'routeId es obligatorio para el tracking nativo' });
        return () => undefined;
      }

      const handle = await RoisBackgroundLocation.addListener('location', (point) => {
        onPosition(toGeolocationPosition(point));
      });

      try {
        await startNativeService(routeId);
      } catch (error) {
        await handle.remove();
        const message = error instanceof Error ? error.message : String(error);
        // El plugin rechaza con un CÓDIGO estable (`PERMISSION_DENIED`) y un
        // mensaje traducido. Antes esto miraba el mensaje —
        // `message.includes('PERMISSION')`— y nunca acertaba: el texto real es
        // "Permiso de ubicación denegado", que en español NO contiene
        // "PERMISSION". Resultado: el permiso se denegaba, se mapeaba a código
        // 2 (genérico) y el modal de "GPS requerido" no se abría jamás. El
        // operario rechazaba el diálogo del sistema y la app se quedaba muda,
        // sin decirle qué hacer.
        //
        // El código es el contrato; el mensaje es para leerlo un humano y
        // cambia con el idioma.
        const code = (error as { code?: string } | undefined)?.code;
        const denied =
          code === 'PERMISSION_DENIED' || /permission/i.test(message);
        // code 1 = PERMISSION_DENIED en la Geolocation API web; el hook ya
        // sabe abrir el modal de "GPS requerido" con ese código.
        onError({ code: denied ? 1 : 2, message });
        return () => undefined;
      }

      return (stopBackgroundService: boolean) => {
        void handle.remove();
        if (stopBackgroundService) void RoisBackgroundLocation.stop();
      };
    },
    getCurrent(onPosition, onError) {
      // El WebView de Capacitor es contexto seguro y el permiso ya está
      // concedido, así que la Geolocation API estándar sirve para la lectura
      // puntual. El plugin solo aporta lo que el navegador no puede: seguir
      // capturando en segundo plano.
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        onError({ code: 2, message: 'Geolocation no disponible' });
        return;
      }
      navigator.geolocation.getCurrentPosition(onPosition, onError, WEB_ONE_SHOT_OPTIONS);
    },
  };
}

export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * @param override fuente inyectada (los tests E2E pasan un mock con la forma
 * de `navigator.geolocation`). Si viene, manda siempre: un test nunca debe
 * acabar hablando con el plugin nativo.
 */
export function getPositionSource(override?: GeolocationLike): PositionSource | null {
  if (override) return createWebPositionSource(override);
  if (isNativeApp()) return createNativePositionSource();
  if (typeof navigator !== 'undefined' && navigator.geolocation) {
    return createWebPositionSource(navigator.geolocation);
  }
  return null;
}

/**
 * Vuelca a IndexedDB lo que el servicio nativo capturó mientras el WebView
 * estaba congelado, dormido o directamente muerto.
 *
 * El orden importa y por eso los tres pasos viven juntos aquí:
 *
 *   1. `drain` lee del buffer SQLite **sin borrar**
 *   2. `appendGpsPoints` escribe en IndexedDB (deduplica por timestamp)
 *   3. `ack` borra del buffer, ya solo lo confirmado
 *
 * Si la app muere entre 1 y 3, el siguiente arranque vuelve a leer los mismos
 * puntos y la deduplicación los absorbe: nunca se pierde ni se duplica nada.
 * Invertir el orden (borrar antes de persistir) sí perdería puntos.
 *
 * @returns cuántos puntos se han incorporado (0 en web).
 */
export async function drainNativeGpsBuffer(routeId: string): Promise<number> {
  if (!isNativeApp() || !routeId) return 0;

  const { points } = await RoisBackgroundLocation.drain({ routeId });
  if (points.length === 0) return 0;

  await appendGpsPoints(routeId, points.map(toLocalGpsPoint));

  const upToTs = points[points.length - 1].timestamp;
  await RoisBackgroundLocation.ack({ routeId, upToTs });

  return points.length;
}

/**
 * Watchdog: se asegura de que el servicio nativo sigue vivo mientras hay tramo
 * activo, y lo revive si no.
 *
 * Hace falta porque las capas de Samsung, Xiaomi, Huawei y compañía matan
 * servicios en segundo plano por su cuenta, y sin esto un solo asesinato del
 * sistema deja la traza cortada **hasta que alguien reabra la app**. Con
 * `ACCESS_BACKGROUND_LOCATION` concedido (checklist de la tablet) el
 * re-arranque también es legal con la app en segundo plano.
 *
 * NO reanuda tras un reinicio del dispositivo: eso exigiría un BroadcastReceiver
 * de BOOT_COMPLETED, y el dispositivo solo sabe qué `routeId` guardó, no si ese
 * recorrido sigue abierto (pueden haberlo cerrado desde sala u otra tablet).
 * Arrancar a ciegas mandaría puntos de un recorrido ya cerrado. La verificación
 * correcta contra el backend ocurre al abrir la app, que es lo que ya pasa.
 *
 * @returns `true` si ha tenido que revivirlo — la frecuencia con la que esto
 * ocurre es la métrica de salud del dispositivo.
 */
export async function ensureNativeTrackingAlive(routeId: string): Promise<boolean> {
  if (!isNativeApp() || !routeId) return false;

  const { running } = await RoisBackgroundLocation.isRunning();
  if (running) return false;

  await startNativeService(routeId);
  return true;
}

/**
 * Apaga un servicio que haya quedado huérfano: sin tramo activo no debe haber
 * captura ni notificación ni gasto de batería.
 *
 * Cubre el caso de que el recorrido se cerrase mientras el operario estaba en
 * otra pantalla (o desde otro dispositivo) y el servicio siguiera corriendo.
 */
export async function stopNativeTrackingIfRunning(): Promise<void> {
  if (!isNativeApp()) return;
  const { running } = await RoisBackgroundLocation.isRunning();
  if (running) await RoisBackgroundLocation.stop();
}
